import { useTranslation } from '@sm360/hooks'
import { updateDrCalculatorShown, updateDrCalculatorStart } from '@sm360/helpers'
import { Tabs } from '@sm360/ui-library'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React from 'react'

import { getQuoteDeskingPaymentOptions } from '../../apis/showroom/showroom.service'
import { Context } from '../../configs/state.manager'
import { generateVehicleFormUrl } from '../../utils/global-functions'
import { payloadPaymentOptionsTransform } from '../../transforms/payloadPaymentOptions.transform'
import CashCard from '../cash/cash-card/CashCard'
import CashInvoice from '../cash/cash-invoice/CashInvoice'
import Container from '../common/container/Container'
import Cta from '../common/cta/Cta'
import FinanceCard from '../finance/finance-card/FinanceCard'
import FinanceInvoice from '../finance/finance-invoice/FinanceInvoice'
import FinanceTerms from '../finance/finance-terms/FinanceTerms'
import LeaseCard from '../lease/lease-card/LeaseCard'
import LeaseInvoice from '../lease/lease-invoice/LeaseInvoice'
import LeaseTerms from '../lease/lease-terms/LeaseTerms'

const TabsCalculator = ({ version }) => {
    const {
        state,
        state: {
            paymentOptions: { purchaseMethod, financeOptions },
            vehicle,
            paymentOptions,
            vehicleInventoryId,
            quoteId,
        },
        dispatch,
    } = React.useContext(Context)

    const { t } = useTranslation()

    React.useEffect(() => {
        updateDrCalculatorShown(quoteId, vehicle, purchaseMethod)
    }, [])

    if (!financeOptions || !financeOptions.length) return null

    const isAlphaVersion = version === 'alpha'

    const financeInfos = financeOptions.find(({ purchaseMethod }) => purchaseMethod === 'finance')
    const leaseInfos = financeOptions.find(({ purchaseMethod }) => purchaseMethod === 'lease')

    let tabActive = 0 // cash
    if (purchaseMethod === 'finance' && financeInfos.isSelected) tabActive = 1 // Finance
    if (purchaseMethod === 'lease' && leaseInfos.isSelected) tabActive = 2 // Lease

    const tabsClassNames = {
        className: 'uk-tabs pc-tabsCalculator',
        selectedTabClassName: 'uk-tabs__tab--selected pc-tabsCalculator__tab--selected',
        selectedTabPanelClassName: 'uk-tabs__tab-panel--selected pc-tabsCalculator__tabPanel--selected',
    }

    const tabListCashClassNames = clsx('pc-tabsCalculator__tab')
    const tabListFinanceClassNames = clsx(
        'pc-tabsCalculator__tab',
        !financeInfos.isAvailable && 'pc-tabsCalculator__tab--disabled'
    )
    const tabListLeaseClassNames = clsx('pc-tabsCalculator__tab', !leaseInfos.isAvailable && 'pc-tabsCalculator__tab--disabled')

    const handleTabClick = async (tabIndex) => {
        try {
            let purchaseMethod = 'cash'
            if (tabIndex === 1) purchaseMethod = 'finance'
            if (tabIndex === 2) purchaseMethod = 'lease'

            const payload = payloadPaymentOptionsTransform({ state, purchaseMethod })
            const initPaymentOptions = await getQuoteDeskingPaymentOptions(payload)

            updateDrCalculatorStart(quoteId, vehicle, paymentOptions?.purchaseMethod)

            dispatch({ type: 'setPaymentOptions', paymentOptions: initPaymentOptions.data })
        } catch (error) {
            console.error(error)
        }
    }

    const createRequestMoreInfoFormUrl = (vehicle) => {
        const formUrl = vehicle.newVehicle
            ? t('pc.vehicle.request.more.info.form.new')
            : t('pc.vehicle.request.more.info.form.used')

        return generateVehicleFormUrl(formUrl, vehicle, 'cash', vehicleInventoryId, {})
    }

    const createFinancingRequestFormUrl = (vehicle, paymentOptions, selectedPurchaseMethod) => {
        const formUrl = vehicle.newVehicle ? t('pc.vehicle.finance.request.form.new') : t('pc.vehicle.finance.request.form.used')

        return generateVehicleFormUrl(formUrl, vehicle, selectedPurchaseMethod, vehicleInventoryId, paymentOptions, true)
    }

    const handleCtaClick = (selectedPurchaseMethod) => {
        let formUrl
        if (selectedPurchaseMethod === 'cash') {
            formUrl = createRequestMoreInfoFormUrl(vehicle)
        } else {
            formUrl = createFinancingRequestFormUrl(vehicle, paymentOptions, selectedPurchaseMethod)
        }
        window.location.href = formUrl
    }

    return (
        <div className='pc-calculator'>
            <Tabs activeIndex={tabActive} onTabClick={handleTabClick} {...tabsClassNames}>
                <div trigger={t('pc.cash')} className={tabListCashClassNames}>
                    <Container hasTwoCol>
                        <CashCard />
                        <CashInvoice version={version} />
                    </Container>
                    {isAlphaVersion && (
                        <Container extraClasses='pc-tabsCalculator__extraCta'>
                            <Cta label={t('pc.cash.cta')} handleClick={() => handleCtaClick('cash')} />
                        </Container>
                    )}
                </div>
                <div trigger={t('pc.finance')} className={tabListFinanceClassNames} disabled={!financeInfos.isAvailable}>
                    <Container hasTwoCol>
                        <FinanceCard />
                        <FinanceTerms />
                    </Container>
                    <Container>
                        <FinanceInvoice version={version} />
                    </Container>
                    {isAlphaVersion && (
                        <Container extraClasses='pc-tabsCalculator__extraCta'>
                            <Cta label={t('pc.finance.cta')} handleClick={() => handleCtaClick('exchange')} />
                        </Container>
                    )}
                </div>
                <div trigger={t('pc.lease')} className={tabListLeaseClassNames} disabled={!leaseInfos.isAvailable}>
                    <Container hasTwoCol>
                        <LeaseCard />
                        <LeaseTerms />
                    </Container>
                    <Container>
                        <LeaseInvoice version={version} />
                    </Container>
                    {isAlphaVersion && (
                        <Container extraClasses='pc-tabsCalculator__extraCta'>
                            <Cta label={t('pc.finance.cta')} handleClick={() => handleCtaClick('lease')} />
                        </Container>
                    )}
                </div>
            </Tabs>
        </div>
    )
}

TabsCalculator.propTypes = {
    version: PropTypes.string,
}

TabsCalculator.defaultProps = {
    version: 'alpha',
}

export default TabsCalculator
